import {
  lineTotalCents,
  parseMoneyToCents,
  roundToCashStep,
  type MoneyInput,
} from '@agos/shared'

/**
 * What the customer is charged.
 *
 * Every number on this screen is one somebody is about to pay, so the order the
 * steps happen in is the specification: the manual discount is rounded to the
 * cash step *before* it is subtracted, and the total is rounded again after.
 * Rounding once instead of twice changes the bill by up to two cents, which is
 * a receipt that does not reconcile.
 *
 * Ported from legacy `pages/new-order.tsx`, in integer cents (§14.7). Legacy
 * assembles all of this out of floats.
 */

export interface CartLine {
  productId: number
  productName: string
  unitPrice: MoneyInput
  quantity: number
  categoryName?: string
  /**
   * Which of the product's sizes — `FRONTEND.md` §9.85, priced by `BACKEND.md` §10A.33.
   *
   * The label as the menu spells it, `"50 cm"`. `unitPrice` already carries that size's price; this
   * is what the payload sends so the server prices it from the same column.
   */
  size?: string
  giftRuleId?: number
  giftRuleType?: string
}

export type DiscountMode = 'percent' | 'fixed'

export function cartSubtotalCents(cart: CartLine[]): number {
  return cart.reduce((total, line) => total + lineTotalCents(line.unitPrice, line.quantity), 0)
}

/**
 * The manual discount a member of staff typed, in cents.
 *
 * Capped at the subtotal — a 200% discount is a typo, not a refund — and
 * rounded to the cash step, because it is subtracted from a number that will
 * be rounded too.
 */
export function manualDiscountCents(
  mode: DiscountMode,
  value: MoneyInput,
  subtotalCents: number,
): number {
  const entered = parseMoneyToCents(value)
  if (entered <= 0) return 0

  const raw =
    mode === 'percent'
      // The typed value is a percentage, not money, so it comes back from
      // `parseMoneyToCents` scaled by 100 — which is exactly the divisor.
      ? (subtotalCents * entered) / 100 / 100
      : entered

  return Math.min(subtotalCents, roundToCashStep(raw))
}

export interface CartTotals {
  subtotalCents: number
  deliveryCents: number
  /**
   * Refundable charges on the goods — §9.182. **Not** part of the subtotal.
   *
   * `OrderCreationService` adds `surchargeTotal` to the order total and keeps the subtotal as the
   * goods, with its own note on why: leaving them out means *"the deposit line prints on the receipt
   * and nobody collects it, which costs the restaurant 15 cents a can."* This screen was omitting
   * them from the total it showed, so a dispatcher quoted a price the till then exceeded.
   */
  depositCents: number
  manualDiscountCents: number
  couponDiscountCents: number
  totalCents: number
}

/**
 * Delivery is charged only on a delivery order — a customer collecting from the
 * counter is not billed for a driver, whatever zone their address falls in.
 */
export function cartTotals({
  cart,
  orderType,
  deliveryFee,
  discountMode,
  discountValue,
  couponDiscount,
  depositCents,
  needsAddress,
}: {
  cart: CartLine[]
  orderType: string
  deliveryFee: MoneyInput
  discountMode: DiscountMode
  discountValue: MoneyInput
  couponDiscount: MoneyInput
  /** §9.182 — the deposits the server will add, in cents. Absent is nought, as it was before. */
  depositCents?: number
  /**
   * Whether the channel requires an address, and therefore charges for the journey - §9.393.
   *
   * `ServerPricing` charges by the zone for any channel with `requiresDeliveryAddress`, so deciding
   * it by the literal `delivery` here quotes a total the till then exceeds - §9.182's own shape.
   * Optional, falling back to the slug check.
   */
  needsAddress?: boolean
}): CartTotals {
  const subtotal = cartSubtotalCents(cart)
  const charges = needsAddress ?? orderType === 'delivery'
  const delivery = charges ? parseMoneyToCents(deliveryFee) : 0
  const manual = manualDiscountCents(discountMode, discountValue, subtotal)
  const coupon = parseMoneyToCents(couponDiscount)
  const deposits = depositCents ?? 0

  return {
    subtotalCents: subtotal,
    deliveryCents: delivery,
    depositCents: deposits,
    manualDiscountCents: manual,
    couponDiscountCents: coupon,
    // Never below zero: a coupon worth more than the cart does not pay the
    // customer. Deposits are added, not discounted: they are money the customer hands over.
    totalCents: roundToCashStep(Math.max(0, subtotal + delivery + deposits - manual - coupon)),
  }
}

/** A product as `/products` sends it, for the deposits attached to it. */
interface ProductWithSurcharges {
  id?: number
  surcharges?: Array<{ amount?: unknown }>
}

/**
 * What the deposits on this cart come to — §9.182.
 *
 * Mirrors `OrderCreationService.surchargeTotal` deliberately, including the two rules that are easy
 * to get wrong: it is **per unit**, so three cans is three deposits; and a line with an id of nought
 * or less carries none, because a composed line and a gift have no product row to hang one on.
 *
 * `depositCentsOf` in `@agos/customer-core` is the same rule for the customer app. Two copies rather
 * than one shared: the two carts are different types and neither module may import the other's.
 */
export function cartDepositCents(
  cart: readonly CartLine[],
  products: readonly ProductWithSurcharges[],
): number {
  let total = 0
  for (const line of cart) {
    if (line.productId <= 0 || line.giftRuleId !== undefined) continue
    const product = products.find((candidate) => candidate.id === line.productId)
    for (const surcharge of product?.surcharges ?? []) {
      total += parseMoneyToCents(surcharge.amount as string | number | null) * line.quantity
    }
  }
  return total
}
